import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "../features/auth/api";

export function ForgotPasswordPage() {
  const [companyCode, setCompanyCode] = useState("");
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const isDisabled = useMemo(
    () => submitting || companyCode.trim().length === 0 || username.trim().length === 0,
    [companyCode, submitting, username],
  );

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await requestPasswordReset(companyCode.trim().toUpperCase(), username.trim());
      setMessage(result.message);
    } catch (requestError) {
      // Same generic message even on a request failure -- the backend
      // already never distinguishes "no such account" from "sent", so a
      // network/validation error shouldn't leak more than that either.
      setMessage(
        requestError instanceof Error
          ? requestError.message
          : "If that account exists and has an email on file, a password reset link has been sent.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="hero fade-in">
        <p className="eyebrow">
          <span className="brand-wordmark">
            0todeploy <span className="brand-vms">VMS</span>
          </span>
        </p>
        <h1>Reset your password</h1>
        <p className="subtitle">
          Enter your company code and username -- if the account has an email on file, we'll send a
          reset link.
        </p>
      </section>

      <section className="panel auth-card fade-in delay-1">
        <header className="panel-header">
          <h2>Forgot Password</h2>
        </header>
        {message ? (
          <>
            <p className="success-banner">{message}</p>
            <p className="auth-switch">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <form className="auth-form" onSubmit={onSubmit}>
            <label htmlFor="forgot-company-code">Company Code</label>
            <input
              id="forgot-company-code"
              name="company-code"
              autoComplete="off"
              value={companyCode}
              onChange={(event) => setCompanyCode(event.target.value)}
              placeholder="e.g. ACME"
              style={{ textTransform: "uppercase" }}
            />

            <label htmlFor="forgot-username">Username</label>
            <input
              id="forgot-username"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Enter your username"
            />

            <button className="refresh-btn auth-submit" type="submit" disabled={isDisabled}>
              {submitting ? "Sending..." : "Send reset link"}
            </button>
            <p className="auth-switch">
              <Link to="/login">Back to sign in</Link>
            </p>
          </form>
        )}
      </section>
    </main>
  );
}
